import { Modal } from "@mui/material";
import ButtonContainer from "commonComponents/Button";
import CommonInput from "commonComponents/CommonInput";
import {
  calculateAndSaveSaleData,
  getClonedSaleDataFromReducer,
} from "helper/saleHelpers";
import { SaleDataType } from "models/sale";
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { LoyaltyModalContext } from "./loyalty-modal-context";
import { businessHelpers, loyaltyHelpers } from "@dotpe/rista-pos-common";
import { currency } from "helper/commonHelpers";

function RistaLoyalty() {
  const { onCancel, onSuccess, resetCallbacks } = useContext(LoyaltyModalContext);
  const [discountsState, setDiscountsState] = useState<any>([]);
  const [editIndex, setEditIndex] = useState<number>(-1);
  const saleData: SaleDataType = useSelector((state: any) => state.saleDataStore.saleData);
  const customerInfo = useSelector((state: any) => state.saleDataStore.saleData?.customerInfo);
  const discountsInSale = useSelector((state: any) => state.saleDataStore.saleData?.billDiscountList);
  const businessData = useSelector((state: any) => state.businessStore?.businessData);
  const allDiscountsInfo = useSelector(
    (state: any) => state?.catalogStore?.discounts
  );
  const totalPoints = useSelector(
    (state: any) => state?.saleDataStore?.saleData?.customerInfo?.loyaltyPoints
  );
  const { setLoyaltyModal } = useContext(LoyaltyModalContext);
  const currencySymbol = currency();
  useEffect(() => {
    setDiscountsState([...discountsInSale]);
  }, []);

  function saveLoyaltyDeals(toggleModal = true) {
    let saleData = getClonedSaleDataFromReducer();
    //need to replace existing loyalty discounts
    const updatedSale = calculateAndSaveSaleData({ ...saleData, billDiscountList: discountsState });
    if (toggleModal) {
      onSuccess?.(updatedSale);
      resetCallbacks();
      setLoyaltyModal(false);
    }
  }

  function onClose() {
    setLoyaltyModal(false);
    onCancel?.();
    resetCallbacks();
  }


  function updateDeal(index: number, points: number) {
    const currentDiscountData = allDiscountsInfo?.[discountsState[index]?.discountId] || {};
    const redemptionRate = loyaltyHelpers.getRedemptionRate(customerInfo, currentDiscountData);
    const amount = redemptionRate * points;
    const updatedData = [...discountsState];
    updatedData[index] = {
      ...updatedData[index],
      ...currentDiscountData, // add missing discount info
      isOverridden: true,
      loyaltyPoints: points,
      amount: -1 * amount,
      netAmount: -1 * amount,
      useLoyalty: true,
    };
    setDiscountsState(updatedData);
  }

  function toggleEditDeal(index: number) {
    if (editIndex === -1) {
      setEditIndex(index);
      return;
    }
    if (index === editIndex) {
      saveLoyaltyDeals(false);
      setEditIndex(-1);
    }
  }

  function dealDataInput(
    deal: any,
    index: number
  ) {
    return editIndex !== index ? (
      <div className="flex items-center gap-[12px] bg-[#51B02F1A] rounded-md text-[#51B02F] p-[12px] w-full relative">
        <div className="border rounded-md border-[#51B02F] p-2">
          {deal.loyaltyPoints} points
        </div>
        <div>
          <span className="font-semibold">{currencySymbol}{deal.amount * -1} OFF</span>
        </div>
      </div>
    ) : (
      <CommonInput
        otherProps={{
          type: "number",
          placeholder: "Enter Loyalty Points",
        }}
        className="border border-black/10 rounded-lg p-[12px] w-full relative"
        value={deal.loyaltyPoints}
        onChange={(e: any) =>
          updateDeal(
            index,
            e.target.value.replace(/[^\w\s]/gi, ''), //replace special characters
          )
        }
      />
    );
  }
  function renderDeals() {
    return discountsState?.map((deal: any, index: number) => {
      const currentDiscountInfo = allDiscountsInfo[deal?.discountId || ""] || {},
        maxAmount = currentDiscountInfo?.maxDiscountAmount,
        redemptionRate = loyaltyHelpers.getRedemptionRate(customerInfo, currentDiscountInfo),
        isValidPoints = loyaltyHelpers.checkValidPoints(deal.loyaltyPoints, currentDiscountInfo, saleData, redemptionRate),
        maxPoints = loyaltyHelpers.getMaxPointsForDeal(currentDiscountInfo, saleData, redemptionRate);
      //filter out non loyalty deals
      if (deal.dclass !== "Deal" || !currentDiscountInfo?.useLoyalty)
        return <></>
      const isEditMode = editIndex === index;
      //if we are in edit mode, then check for valid points
      const allowToggleEdit = !isEditMode || isValidPoints;

      return (
        <div className="flex flex-col py-20 border-b" key={index}>
          <div className="flex gap-4 pb-16 relative items-center">
            <div className="text-[#3F5B8A] font-semibold">
              {deal.provider ? deal.provider + " -" : ""} {deal.discountCode}
            </div>
            <div className="text-14 font-normal">
              Total Points: {totalPoints}
            </div>
            {deal.authenticateCustomer && businessHelpers.isAuthenticationEnabled(businessData) && (
              <div className="absolute right-0 flex items-center text-center ml-auto border border-[#CD6011] rounded-[3px] px-[4px] text-[11px] text-[#CD6011]">
                OTP Required
              </div>
            )}
          </div>
          <div className="flex">
            <div className="w-1/2 flex flex-col">
              <div className="text-[18px] font-semibold">
                Redeemable Points :{maxPoints}
              </div>
              <div className="opacity-50">(1 Loyalty Point= {currencySymbol}{redemptionRate}) {!!maxAmount && `Max amount: ${maxAmount}`} </div>
            </div>
            <div className="w-1/2 flex relative">
              {dealDataInput(deal, index)}
              <button
                disabled={!allowToggleEdit}
                onClick={() => toggleEditDeal(index)}
                className={`text-[#397EF5] font-semibold ${!allowToggleEdit ? "opacity-50" : ""} absolute right-[5%] top-[25%]`}
              >
                {isEditMode ? "Update points" : "Edit"}
              </button>
            </div>
          </div>
        </div>
      );
    });
  }

  return (
    <Modal open={true} className="z-[1001]">
      <div
        className={`modal-center outline-0 min-w-[667px] px-32 max-h-[450px] overflow-hidden`}
      >
        <div className="modal-header font-semibold text-24 flex flex-col gap-2">
          <div>Redeem Loyalty points</div>
          <div className="opacity-70 text-14 font-normal">
            Redeem loyalty points for the following deals
          </div>
        </div>
        <div className="modal-body-content flex flex-col gap-4 mt-20 max-h-[calc(100vh-100px)] pb-[200px]">
          {renderDeals()}
        </div>
        <div className="modal-footer flex sm:px-16 w-full gap-8">
          <ButtonContainer
            text={"Cancel"}
            variantVal={"outlined"}
            parentFunc={onClose}
            size="large"
            customClass="ml-auto"
          />
          <ButtonContainer
            text={"Redeem"}
            variantVal={"contained"}
            parentFunc={saveLoyaltyDeals}
            disabled={editIndex !== -1}
            size="large"
          />
        </div>
      </div>
    </Modal>
  );
}

export default RistaLoyalty;
